import { toast } from "react-toastify";
import { BACKEND_URL } from "../../../../constants/urls";
import {
    BaseComponentProps,
    createComponentBase,
} from "../../../../libs/components";
import { formattedDateTime } from "../../../../utils";
import { Icon } from "../../../atoms/icon";
import { Loader } from "../../../atoms/loader";
import { MultipleFileUpload } from "../../../atoms/multipleFileUpload";
import { RichTextEditor } from "../../../atoms/richTextEditor";
import { payloadType, ResponseType } from "../../../../types";
import { useAddCommentMutation } from "../../../../store/requestManagement/requestSlice";
import React from "react";
import { SOMETHING_WENT_WRONG } from "../../../../constants/constants";

export interface CommentsProps extends BaseComponentProps {
    requestId: string;
    comments: any;
    setComments: any;
}

export const Comments = ({
    requestId,
    comments,
    setComments,
    className,
}: CommentsProps) => {
    const componentClassName = "o-comments";
    const { Container } = createComponentBase(componentClassName, "div");
    const modifiers = {};

    const initialPayload: payloadType = {
        requestId: {
            value: requestId,
            isError: false,
            error: "",
        },
        comment: {
            value: "",
            isError: false,
            error: "",
        },
        attachments: {
            value: [],
            isError: false,
            error: "",
        },
    };

    const [payload, setPayload] = React.useState(initialPayload);
    const [isAddComment, setIsAddComment] = React.useState(false);

    React.useEffect(() => {
        setPayload({
            ...payload,
            requestId: { value: requestId, isError: false, error: "" },
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [requestId]);

    const [addCommentApi, { isLoading: isCommentLoading }] =
        useAddCommentMutation();

    const submitHandler = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        // $rules['attachments.*'] = ['file', 'mimes:jpeg,jpg,png,webp,pdf,doc,docx,xls,xlsx,ppt,pptx,zip', 'max:2048'];
        const validateAttachments = () => {
            const errors: string[] = [];

            const attachments = payload.attachments?.value as File[] | undefined;
            if (!attachments || !attachments.length) return errors;

            const allowedExtensions = /\.(jpeg|jpg|png|webp|pdf|doc|docx|xls|xlsx|ppt|pptx|zip|csv)$/i;
            const maxSize = 2 * 1024 * 1024; // 2MB

            attachments.forEach((file: File) => {
                if (!allowedExtensions.test(file.name)) {
                    errors.push(`File "${file.name}" is not a valid format.`);
                }else if (file.size > maxSize) {
                    errors.push(`File "${file.name}" exceeds the maximum size of 2MB.`);
                }
            });

            return errors;
        };

        const attachmentErrors = validateAttachments();

        if (payload.comment.value === "") {
            setPayload({
                ...payload,
                comment: {
                    ...payload.comment,
                    isError: true,
                    error: "Comment is required",
                },
            });
            if (attachmentErrors.length > 0) {
                setPayload({
                    ...payload,
                    attachments: {
                        ...payload.attachments,
                        isError: true,
                        error: attachmentErrors.join(", "),
                    },
                });
            }
            return;
        }

        if (attachmentErrors.length > 0) {
            setPayload({
                ...payload,
                attachments: {
                    ...payload.attachments,
                    isError: true,
                    error: attachmentErrors.join(", "),
                },
            });
            return;
        }

        const formData = new FormData();
        formData.append("id", payload.requestId.value as string);
        formData.append("comment", payload.comment.value as string);
        (payload.attachments.value as File[]).forEach((file: File) => {
            formData.append("attachments[]", file);
        });

        const response = (await addCommentApi(formData)) as ResponseType;
        if (response?.data?.success) {
            toast.success(response?.data?.message);
            setPayload({
                ...initialPayload,
                requestId: {
                    value: payload.requestId.value,
                    isError: false,
                    error: "",
                },
            });
            setComments(response?.data?.result?.comments);
        } else {
            if (response?.error?.data?.errors) {
                Object.keys(response?.error?.data?.errors).forEach((key) => {
                    setPayload((prevState) => ({
                        ...prevState,
                        [key]: {
                            ...prevState[key],
                            error: response?.error?.data?.errors[key][0],
                            isError: true,
                        },
                    }));
                });
            } else {
                toast.error(response?.error?.data?.message);
            }
            if (response?.error?.data?.error) {
                toast.error(SOMETHING_WENT_WRONG);
            }
        }
    };

    return (
        <Container className={className} modifiers={modifiers}>
            <div className={`${componentClassName}__group`}>
                <p className={`${componentClassName}__group__title`}>Comments</p>
                {!isAddComment && (
                    <button
                        className={`${componentClassName}__group__button`}
                        onClick={() => setIsAddComment(!isAddComment)}
                    >
                        Add a comment
                    </button>
                )}
            </div>
            {isAddComment && (<Loader isLoading={isCommentLoading}>

                <form
                    className={`${componentClassName}__footer`}
                    onSubmit={submitHandler}
                >
                    <p className={`${componentClassName}__footer__title`}>
                        Add a comment
                    </p>
                    <RichTextEditor
                        className={`${componentClassName}__footer__input`}
                        placeholder="Write a comment..."
                        value={payload.comment.value as string}
                        onChanged={(event) => {
                            setPayload({
                                ...payload,
                                comment: { value: event, isError: false, error: "" },
                            });
                        }}
                        isError={payload.comment.isError}
                        error={payload.comment.error}
                    />
                    <MultipleFileUpload
                        onUpload={(files) => {
                            setPayload({
                                ...payload,
                                attachments: { value: files, isError: false, error: "" },
                            });
                        }}
                        value={payload.attachments.value as File[]}
                        placeholder="Please select attachments"
                        isError={payload.attachments.isError}
                        error={payload.attachments.error}
                    />
                    <div className={`${componentClassName}__footer__action`}>
                        <button
                            className={`${componentClassName}__footer__button`}
                            type="submit"
                            disabled={isCommentLoading}
                        >
                            Comment
                        </button>
                        <button
                            className={`${componentClassName}__footer__button ${componentClassName}__footer__button--cancel`}
                            type="button"
                            onClick={() => {
                                setIsAddComment(false);
                                setPayload({
                                    ...initialPayload,
                                    requestId: {
                                        value: payload.requestId.value,
                                        isError: false,
                                        error: "",
                                    },
                                });
                            }}
                        >
                            Cancel
                        </button>
                    </div>
                </form>
            </Loader>)}

            {comments.length > 0 ? (
                comments?.map((comment: any, index: number) => (
                    <div className={`${componentClassName}__container`} key={index}>
                        <div className={`${componentClassName}__header`}>
                            <div className={`${componentClassName}__username`}>
                                {comment?.user?.profilePhotoPath && (
                                    <img
                                        src={BACKEND_URL + "/" + comment?.user?.profilePhotoPath}
                                        alt="avatar"
                                        className={`${componentClassName}__username__avatar`}
                                    />
                                )}
                                <p>
                                    {comment?.user?.middleName
                                        ? `${comment?.user?.firstName} ${comment?.user?.middleName} ${comment?.user?.lastName}`
                                        : `${comment?.user?.firstName} ${comment?.user?.lastName}`}
                                </p>
                            </div>

                            <p className={`${componentClassName}__date`}>
                                {formattedDateTime(comment?.createdAt)}
                            </p>
                        </div>
                        <p
                            style={{
                                whiteSpace: 'pre-line',
                                overflowWrap: 'break-word',
                                wordWrap: 'break-word',
                                wordBreak: 'break-all'
                            }}
                            className={`${componentClassName}__content`}
                            dangerouslySetInnerHTML={{ __html: comment?.comment }}
                        />
                        <div className={`${componentClassName}__files`}>
                            {comment?.attachments?.map((file: any, index: number) => (
                                <a
                                    href={file.basePath + "/" + file?.filePath}
                                    target="_blank"
                                    rel="noreferrer"
                                    key={index}
                                    className={`${componentClassName}__files__file`}
                                >
                                    {file?.fileName}
                                </a>
                            ))}
                        </div>
                    </div>
                ))
            ) : (
                <div className={`${componentClassName}__notFound`}>
                    <p>No comments found!</p>
                    <Icon icon="sadFace" size="tertiary" />
                </div>
            )}
        </Container>
    );
};
